<template>
  <FlashNotifications ref="flash" />

  <!-- Turn off confirmation -->
  <Transition name="modal">
    <div v-if="confirmOff" class="fixed inset-0 z-50 flex items-center justify-center p-4" @click.self="confirmOff = false">
      <div class="absolute inset-0 bg-black/40 backdrop-blur-sm"></div>
      <div class="relative bg-white dark:bg-[#1C2128] rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 max-w-sm w-full p-6 text-center">
        <div class="w-12 h-12 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mx-auto mb-4">
          <svg class="w-6 h-6 text-red-500" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"/>
          </svg>
        </div>
        <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-2">Turn off Job Tracker?</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-6">
          This disconnects every browser and removes Jobs from your sidebar. You can set it up again at any time.
        </p>
        <div class="flex gap-3">
          <button type="button" @click="confirmOff = false"
            class="flex-1 px-4 py-2 text-sm font-semibold rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800">
            Keep it on
          </button>
          <button type="button" @click="disable" :disabled="toggling"
            class="flex-1 px-4 py-2 text-sm font-semibold rounded-lg bg-red-600 hover:bg-red-700 text-white disabled:opacity-60">
            {{ toggling ? 'Turning off...' : 'Turn off' }}
          </button>
        </div>
      </div>
    </div>
  </Transition>

  <div class="min-h-app-screen bg-gray-50 dark:bg-[#0d1117]">
    <!-- Header -->
    <div class="bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700">
      <div class="flex items-center gap-3 px-4 sm:px-6 py-3.5">
        <RouterLink to="/integrations" data-turbo-prefetch="false"
          class="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          aria-label="Back to integrations">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5"/>
          </svg>
        </RouterLink>
        <h1 class="text-xl font-bold text-gray-900 dark:text-white">Job Tracker</h1>
        <span v-if="!loading && !error"
          class="ml-1 inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full"
          :class="enabled
            ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
            : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'">
          <span class="w-1.5 h-1.5 rounded-full" :class="enabled ? 'bg-green-500' : 'bg-gray-400'"></span>
          {{ enabled ? 'Enabled' : 'Not enabled' }}
        </span>
      </div>
    </div>

    <div class="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-4 pb-24">

      <!-- Loading -->
      <div v-if="loading" class="animate-pulse space-y-4">
        <div class="h-24 bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
        <div class="h-40 bg-gray-100 dark:bg-gray-800 rounded-xl"></div>
        <div class="h-56 bg-gray-100 dark:bg-gray-800 rounded-xl"></div>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="flex flex-col items-center justify-center py-32 text-center px-4">
        <div class="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
          <svg class="w-8 h-8 text-red-500" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/>
          </svg>
        </div>
        <h3 class="text-base font-semibold text-gray-900 dark:text-white">Something went wrong</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">{{ error }}</p>
        <button @click="retry" class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold">Try again</button>
      </div>

      <template v-else>
        <!-- Intro -->
        <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 px-6 py-5 flex items-start gap-4">
          <img :src="integration?.logo" alt="Job Tracker" class="w-12 h-12 rounded-xl flex-shrink-0">
          <div>
            <h2 class="font-semibold text-gray-900 dark:text-white">Save jobs from job sites into Timelydo</h2>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
              The Timelydo Jobs Chrome extension reads the jobs you search for and sends them to your account.
              Set it up in three steps. A Jobs section appears in your sidebar once the integration is enabled.
              <a href="/help-center/job-tracker/complete-guide" target="_blank" rel="noopener noreferrer" data-turbo="false"
                class="font-semibold text-blue-600 dark:text-blue-400 hover:underline">Read the complete guide</a>.
            </p>
          </div>
        </div>

        <!-- Steps -->
        <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">

          <div class="px-6 py-5 flex gap-4">
            <span :class="stepCircle(step1Done)" :aria-label="step1Done ? 'Step 1 done' : 'Step 1'">
              <template v-if="step1Done"><svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/></svg></template>
              <template v-else>1</template>
            </span>
            <div class="flex-1">
              <div class="flex items-center gap-3 flex-wrap"><h3 class="font-semibold text-gray-900 dark:text-white">Download the extension</h3><HelpLink path="job-tracker/set-up-the-extension#download" label="Guide" /></div>
              <p class="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-3">
                It is not in the Chrome Web Store yet, so you add it to Chrome yourself. It takes about a minute.
              </p>
              <a :href="DOWNLOAD_PATH" data-turbo="false"
                class="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold shadow-sm">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"/>
                </svg>
                Download timelydo-jobs.zip
              </a>
            </div>
          </div>

          <div class="px-6 py-5 flex gap-4">
            <span :class="stepCircle(step2Done)" :aria-label="step2Done ? 'Step 2 done' : 'Step 2'">
              <template v-if="step2Done"><svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/></svg></template>
              <template v-else>2</template>
            </span>
            <div class="flex-1">
              <div class="flex items-center gap-3 flex-wrap"><h3 class="font-semibold text-gray-900 dark:text-white">Add it to Chrome</h3><HelpLink path="job-tracker/set-up-the-extension#add-to-chrome" label="Step by step guide" /></div>
              <ol class="mt-2 space-y-1.5 text-sm text-gray-600 dark:text-gray-300 list-decimal list-inside">
                <li>Unzip the file you downloaded. You get a folder called <span class="font-mono text-[13px]">timelydo-jobs</span>.</li>
                <li>
                  In Chrome, open
                  <a href="chrome://extensions" target="_blank" rel="noopener noreferrer" data-turbo="false"
                    class="font-mono text-[13px] text-blue-600 dark:text-blue-400 hover:underline">chrome://extensions</a>.
                  <button type="button" @click="copyExtensionsUrl"
                    class="ml-1 px-2 py-0.5 text-xs font-medium rounded-md border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-blue-300 hover:text-blue-600 transition-colors">
                    {{ copied ? 'Copied' : 'Copy' }}
                  </button>
                  <span class="block text-xs text-gray-400 dark:text-gray-500 mt-1 ml-5">
                    If the link does not open, Chrome has blocked it. Copy the address and paste it into a new tab.
                  </span>
                </li>
                <li>Turn on <span class="font-semibold">Developer mode</span> in the top right corner.</li>
                <li>Click <span class="font-semibold">Load unpacked</span> and choose the <span class="font-mono text-[13px]">timelydo-jobs</span> folder.</li>
                <li>Click the puzzle piece icon in the toolbar and pin <span class="font-semibold">Timelydo</span>.</li>
              </ol>
              <p class="text-xs text-gray-400 dark:text-gray-500 mt-3">
                Keep the folder where it is. Chrome reads the extension from it every time it starts.
              </p>

              <div class="mt-4 flex items-center gap-2 text-sm" role="status">
                <template v-if="detection.status.value === 'installed'">
                  <span class="inline-flex items-center gap-1.5 font-medium px-2.5 py-1 rounded-full bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/></svg>
                    Extension detected
                    <span v-if="detection.version.value" class="font-normal opacity-80">version {{ detection.version.value }}</span>
                  </span>
                </template>
                <template v-else-if="detection.status.value === 'checking'">
                  <span class="text-gray-400 dark:text-gray-500">Looking for the extension...</span>
                </template>
                <template v-else-if="detection.status.value === 'unsupported'">
                  <span class="text-gray-500 dark:text-gray-400">
                    This browser cannot detect the extension. Open this page in Chrome to set up Job Tracker.
                  </span>
                </template>
                <template v-else>
                  <span class="text-gray-500 dark:text-gray-400">Not detected yet. This updates when you come back to this tab.</span>
                  <button type="button" @click="detection.check" class="font-semibold text-blue-600 dark:text-blue-400 hover:underline">Check again</button>
                </template>
              </div>
            </div>
          </div>

          <div class="px-6 py-5 flex gap-4">
            <span :class="stepCircle(step3Done)" :aria-label="step3Done ? 'Step 3 done' : 'Step 3'">
              <template v-if="step3Done"><svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/></svg></template>
              <template v-else>3</template>
            </span>
            <div class="flex-1">
              <div class="flex items-center gap-2 flex-wrap">
                <h3 class="font-semibold text-gray-900 dark:text-white">Connect your account</h3>
                <HelpLink path="job-tracker/set-up-the-extension#connect" label="Guide" />
                <span v-if="connections.length"
                  class="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400">
                  <span class="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                  Connected
                </span>
              </div>
              <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Click the Timelydo icon in Chrome and choose Connect. You approve access on Timelydo, and that browser is then linked to your account only.
                This page updates when you come back.
              </p>

              <ul v-if="connections.length" class="mt-4 divide-y divide-gray-100 dark:divide-gray-700 border border-gray-100 dark:border-gray-700 rounded-lg">
                <li v-for="connection in connections" :key="connection.id" class="flex items-center justify-between gap-3 px-4 py-3">
                  <div class="min-w-0">
                    <p class="text-sm font-medium text-gray-900 dark:text-white truncate">{{ connection.name || 'Chrome' }}</p>
                    <p class="text-xs text-gray-400 dark:text-gray-500">
                      Version {{ connection.extension_version || 'unknown' }}. {{ lastUsed(connection) }}
                    </p>
                  </div>
                  <button type="button" @click="disconnect(connection)" :disabled="disconnectingId === connection.id"
                    class="flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-red-300 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-60">
                    {{ disconnectingId === connection.id ? '...' : 'Disconnect' }}
                  </button>
                </li>
              </ul>
              <button v-else type="button" @click="loadConnections"
                class="mt-3 text-sm font-semibold text-blue-600 dark:text-blue-400 hover:underline">
                Check again
              </button>
            </div>
          </div>
        </div>

        <!-- Enable -->
        <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 px-6 py-5 flex items-center justify-between gap-4">
          <div>
            <div class="flex items-center gap-3 flex-wrap"><h3 class="font-semibold text-gray-900 dark:text-white">Enable Job Tracker</h3><HelpLink path="job-tracker/set-up-the-extension#sidebar" label="What appears in the sidebar" /></div>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {{ enabled ? 'The Jobs section is showing in your sidebar.' : canEnable ? 'Turn this on to show the Jobs section in your sidebar.' : 'Connect the extension first. This switch unlocks once it is connected.' }}
            </p>
          </div>
          <button type="button" role="switch" :aria-checked="enabled" :disabled="!canToggle" @click="toggle"
            aria-label="Enable Job Tracker"
            class="relative w-11 h-6 rounded-full flex-shrink-0 transition-colors"
            :class="[enabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600', canToggle ? '' : 'opacity-60 cursor-not-allowed']">
            <span class="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform"
              :class="enabled ? 'translate-x-5' : ''"></span>
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { RouterLink } from 'vue-router'
import { useIntegrationsStore } from 'stores/integrations.js'
import { useRequest } from 'composables/useRequest.js'
import { useExtensionDetection } from 'composables/useExtensionDetection.js'
import FlashNotifications from 'controllers/vue_components/flash_toast'
import HelpLink from 'controllers/vue_components/shared/HelpLink.vue'

const DOWNLOAD_PATH = '/extensions/timelydo-jobs'
const flash = ref(null)
const { axiosRequest } = useRequest(flash)
const store = useIntegrationsStore()

const loading = computed(() => store.loading || (store.fetchedAt === null && !store.error))
const error = computed(() => store.error)
const integration = computed(() => store.integrations.find((i) => i.locale_name === 'job_tracker'))
const enabled = computed(() => Boolean(integration.value?.user_integration_id))

const connections = ref([])

// ── Setup progress ──
const detection = useExtensionDetection()

// Having a connected browser proves the earlier steps happened, even where detection is
// not possible (Firefox) or the extension lives in another Chrome profile.
const step1Done = computed(() => detection.status.value === 'installed' || connections.value.length > 0)
const step2Done = computed(() => detection.status.value === 'installed' || connections.value.length > 0)
const step3Done = computed(() => connections.value.length > 0)

function stepCircle(done) {
  return [
    'w-7 h-7 rounded-full text-sm font-bold flex items-center justify-center flex-shrink-0 transition-colors',
    done ? 'bg-green-500 text-white' : 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
  ]
}

// ── Connected browsers ──
const disconnectingId = ref(null)

async function loadConnections() {
  const res = await axiosRequest('GET', '/api/v1/extension_connections', {}, false)
  if (!res.js_error && res.response?.success) connections.value = res.response.data
}

async function disconnect(connection) {
  disconnectingId.value = connection.id
  const res = await axiosRequest('DELETE', `/api/v1/extension_connections/${connection.id}`, {}, false)
  disconnectingId.value = null
  if (!res.js_error && res.response?.success) {
    connections.value = connections.value.filter((c) => c.id !== connection.id)
    flash.value?.show('Browser disconnected.', 'notice')
  } else {
    flash.value?.show(res.response?.message || 'Failed to disconnect.', 'alert')
  }
}

function lastUsed(connection) {
  if (!connection.last_used_at) return 'Not used yet.'
  const minutes = Math.round((Date.now() - new Date(connection.last_used_at).getTime()) / 60000)
  if (minutes < 1) return 'Used just now.'
  if (minutes < 60) return `Used ${minutes} minute${minutes === 1 ? '' : 's'} ago.`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `Used ${hours} hour${hours === 1 ? '' : 's'} ago.`
  const days = Math.round(hours / 24)
  return `Used ${days} day${days === 1 ? '' : 's'} ago.`
}

// ── Enable switch ──
const toggling = ref(false)
const confirmOff = ref(false)
const canEnable = computed(() => connections.value.length > 0)
const canToggle = computed(() => !toggling.value && (enabled.value || canEnable.value))

function toggle() {
  if (enabled.value) confirmOff.value = true
  else enable()
}

async function enable() {
  toggling.value = true
  const res = await axiosRequest('POST', '/integrations/job_tracker/enable', {}, false)
  toggling.value = false
  if (!res.js_error && res.response?.success) {
    store.setAll(res.response.data.current_user_integrations)
    flash.value?.show(res.response.message, 'notice')
  } else {
    flash.value?.show(res.response?.message || 'Failed to enable Job Tracker.', 'alert')
  }
}

async function disable() {
  toggling.value = true
  const res = await axiosRequest('DELETE', '/integrations/job_tracker/disable', {}, false)
  toggling.value = false
  confirmOff.value = false
  if (!res.js_error && res.response?.success) {
    store.setAll(res.response.data.current_user_integrations)
    connections.value = []
    flash.value?.show(res.response.message, 'notice')
  } else {
    flash.value?.show(res.response?.message || 'Failed to turn off Job Tracker.', 'alert')
  }
}

// ── Chrome address helper ──
const copied = ref(false)

async function copyExtensionsUrl() {
  try {
    await navigator.clipboard.writeText('chrome://extensions')
    copied.value = true
    setTimeout(() => { copied.value = false }, 1500)
  } catch {
    // Clipboard access can be denied; the address is shown on the page to copy by hand.
  }
}

// The user connects in the extension, then comes back to this tab, so refresh on focus.
onMounted(() => {
  store.fetchIfStale()
  loadConnections()
  window.addEventListener('focus', loadConnections)
})
onBeforeUnmount(() => window.removeEventListener('focus', loadConnections))

async function retry() {
  store.invalidate()
  await store.fetchIfStale()
  await loadConnections()
}
</script>
